import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from 'antd';
import { api } from '../api';
import { t } from '../i18n';

type DeviceLite = { id: string; lastError: string | null; lastErrorAt: number | null };

/**
 * Opening a device's disks always reads them LIVE: ask the device to re-scan,
 * wait for the fresh inventory it pushes, then hand back that session. Shared by
 * the admin and client device tables (which navigate to the session) and by the
 * Disks workspace (which renders the editor inline). Offline devices can't be
 * opened.
 */
export function useLiveDisks(listFn: () => Promise<DeviceLite[]>) {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [openingFor, setOpeningFor] = useState<string | null>(null);
  // Cleared on unmount so the poll loop bails before navigate()/setState after
  // the page is gone (which would otherwise hijack navigation).
  const aliveRef = useRef(true);
  useEffect(() => () => { aliveRef.current = false; }, []);

  /**
   * Trigger the live re-scan and resolve the id of the fresh inventory session,
   * or null on offline / read-failure / timeout (each surfaces its own message).
   */
  const resolveDisks = async (
    deviceId: string,
    baselineErrorAt: number | null,
  ): Promise<string | null> => {
    setOpeningFor(deviceId);
    try {
      const before = await api.listSessions({ deviceId, limit: 1 });
      const beforeCreated = before.items[0]?.createdAt ?? 0;
      const r = await api.requestReinventory(deviceId);
      if (!r.online) {
        if (aliveRef.current) message.warning(t('deviceOfflineRead'));
        return null;
      }
      const deadline = Date.now() + 40_000;
      while (aliveRef.current && Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, 1000));
        if (!aliveRef.current) return null;
        const [list, devices] = await Promise.all([
          api.listSessions({ deviceId, limit: 1 }),
          listFn(),
        ]);
        const latest = list.items[0];
        if (latest && latest.createdAt > beforeCreated) {
          return latest.id;
        }
        // Stop early if the device already reported why it failed, instead
        // of always waiting out the full timeout with no explanation.
        const dev = devices.find((d) => d.id === deviceId);
        if (dev?.lastErrorAt && dev.lastErrorAt !== baselineErrorAt) {
          if (aliveRef.current) message.error(dev.lastError || t('deviceReadTimeout'));
          return null;
        }
      }
      if (aliveRef.current) message.warning(t('deviceReadTimeout'));
      return null;
    } catch (e) {
      if (aliveRef.current) message.error((e as Error).message);
      return null;
    } finally {
      if (aliveRef.current) setOpeningFor(null);
    }
  };

  // Navigate to the fresh session (device tables).
  const openDisks = async (deviceId: string, baselineErrorAt: number | null) => {
    const sid = await resolveDisks(deviceId, baselineErrorAt);
    if (sid && aliveRef.current) navigate(`/sessions/${sid}`);
  };

  return { openDisks, resolveDisks, openingFor };
}
